/**
 * Client — browser-side Colyseus connection manager.
 *
 * Handles connecting to the game server, joining/leaving rooms,
 * and dispatching server messages to the Game instance.
 *
 * @deprecated Use GameTransport interface instead. Kept only for
 * ColyseusTransport adapter during migration to edge Workers.
 */

import type * as Colyseus from 'colyseus.js';

/* eslint-disable @typescript-eslint/no-deprecated */
import type { ServerMessage } from '../../shared/types.js';

export type ServerMessageHandler = (message: ServerMessage) => void;

/* eslint-enable @typescript-eslint/no-deprecated */

export class GameClient {
  private client: Colyseus.Client | null = null;
  private room: Colyseus.Room | null = null;
  private handlers: Set<ServerMessageHandler> = new Set();
  private serverUrl: string;

  constructor(serverUrl = 'ws://localhost:2567') {
    this.serverUrl = serverUrl;
  }

  /** Connect to the Colyseus server */
  async connect(): Promise<void> {
    try {
      const { Client } = await import('colyseus.js');
      this.client = new Client(this.serverUrl);
    } catch {
      // colyseus.js not available — game runs standalone
      throw new Error('colyseus.js unavailable');
    }
  }

  /** Join a room by name (creates if not exists) */
  async joinRoom(roomName: string, options?: Record<string, unknown>): Promise<void> {
    if (!this.client) {
      throw new Error('Not connected. Call connect() first.');
    }

    this.room = await this.client.joinOrCreate(roomName, options);

    /* eslint-disable @typescript-eslint/no-deprecated */
    this.room.onMessage('serverMessage', (message: ServerMessage) => {
      for (const handler of this.handlers) {
        handler(message);
      }
    });
    /* eslint-enable @typescript-eslint/no-deprecated */

    this.room.onLeave((code: number) => {
      console.log(`[GameClient] Left room (code: ${String(code)})`);
      this.room = null;
    });
  }

  /** Leave the current room */
  async leaveRoom(): Promise<void> {
    if (this.room) {
      await this.room.leave();
      this.room = null;
    }
  }

  /** Send a message to the server */
  send(message: { type: string; payload: unknown }): void {
    if (!this.room) {
      console.warn('[GameClient] Not in a room — message not sent');
      return;
    }
    this.room.send('clientMessage', message);
  }

  /** Register a handler for server messages. Returns an unsubscribe function. */
  onMessage(handler: ServerMessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /** Whether we are currently in a room */
  get isInRoom(): boolean {
    return this.room !== null;
  }

  /** Current room session ID */
  get sessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  /** Disconnect entirely */
  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.leave();
      this.room = null;
    }
    this.client = null;
    this.handlers.clear();
  }
}
